/**
 * @module UseNotificationPush
 * @package @geenius/notifications-react-css
 * @description Manages browser notification permissions and push subscription
 * requests for the React vanilla CSS notifications variant.
 */

import { useCallback, useState } from 'react'

/**
 * Push subscription state returned by {@link useNotificationPush}.
 */
export interface UseNotificationPushResult {
  permission: NotificationPermission
  subscription: PushSubscription | null
  requestPermission: () => Promise<NotificationPermission>
  subscribe: (vapidPublicKey?: string) => Promise<PushSubscription | null>
}

/**
 * Requests browser push permission and creates subscriptions when supported.
 *
 * @returns Current permission state plus permission/subscription helpers.
 */
export function useNotificationPush(): UseNotificationPushResult {
  const [permission, setPermission] = useState<NotificationPermission>(
    typeof Notification !== 'undefined' ? Notification.permission : 'default',
  )
  const [subscription, setSubscription] = useState<PushSubscription | null>(null)

  const requestPermission = useCallback(
    async (): Promise<NotificationPermission> => {
      if (typeof Notification === 'undefined') {
        return 'denied'
      }

      const result = await Notification.requestPermission()
      setPermission(result)
      return result
    },
    [],
  )

  const subscribe = useCallback(
    async (vapidPublicKey?: string): Promise<PushSubscription | null> => {
      if (!('serviceWorker' in navigator) || !vapidPublicKey) {
        return null
      }

      const registration = await navigator.serviceWorker.ready
      const nextSubscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: vapidPublicKey,
      })

      setSubscription(nextSubscription)
      return nextSubscription
    },
    [],
  )

  return { permission, subscription, requestPermission, subscribe }
}
